import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Tabs,
  Tab,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import AddIcon from '@mui/icons-material/Add';

/**
 * Set Management Dialog Component
 * Dialog for managing saved filter sets
 *
 * @param {boolean} open - Whether dialog is open
 * @param {Function} onClose - Handler for closing dialog
 * @param {Array} savedSets - All saved sets
 * @param {number} activeSetTab - Active tab index
 * @param {Function} setActiveSetTab - Active tab setter
 * @param {Function} handleLoadSet - Handler for loading set
 * @param {Function} getSetTypeBadge - Get set type badge label
 */
const SetManagementDialog = ({
  open,
  onClose,
  savedSets,
  activeSetTab,
  setActiveSetTab,
  handleLoadSet,
  getSetTypeBadge,
}) => {
  const filterSetsByTab = (set) => {
    if (activeSetTab === 0) return true; // All
    if (activeSetTab === 1) return set.type === 'parameters';
    if (activeSetTab === 2) return set.type === 'stations';
    if (activeSetTab === 3) return set.type === 'tags';
    if (activeSetTab === 4) return set.type === 'mixed';
    return true;
  };

  const filteredSets = savedSets ? savedSets.filter(filterSetsByTab) : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BookmarkIcon />
          Gespeicherte Sets verwalten
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary" paragraph>
          Sets sind vordefinierte Kombinationen von Parametern, Stationen oder Tags, die Sie schnell anwenden können.
        </Typography>

        {/* Tabs for different set types */}
        <Tabs
          value={activeSetTab}
          onChange={(e, v) => setActiveSetTab(v)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Alle Sets" />
          <Tab label="Parameter" />
          <Tab label="Stationen" />
          <Tab label="Tags" />
          <Tab label="Gemischt" />
        </Tabs>

        {/* Tab Content */}
        <Box sx={{ mt: 2 }}>
          {filteredSets.map(set => (
            <Paper
              key={set.id}
              variant="outlined"
              sx={{
                p: 2,
                mb: 2,
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'action.hover' }
              }}
              onClick={() => {
                handleLoadSet(set);
                onClose();
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" component="span">
                    {set.icon}
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {set.name}
                  </Typography>
                  <Chip
                    label={getSetTypeBadge(set.type)}
                    size="small"
                    color={set.color}
                  />
                  {set.isPublic && (
                    <Chip
                      label="Öffentlich"
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLoadSet(set);
                    onClose();
                  }}
                >
                  Laden
                </Button>
              </Box>

              <Typography variant="body2" color="textSecondary" paragraph>
                {set.description}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {/* Show set contents */}
                {set.items.parameters && set.items.parameters.length > 0 && (
                  <Typography variant="caption" color="textSecondary">
                    <strong>Parameter ({set.items.parameters.length}):</strong> {set.items.parameters.join(', ')}
                  </Typography>
                )}
                {set.items.stations && set.items.stations.length > 0 && (
                  <Typography variant="caption" color="textSecondary">
                    <strong>Stationen ({set.items.stations.length}):</strong> {set.items.stations.slice(0, 3).join(', ')}
                    {set.items.stations.length > 3 && ` + ${set.items.stations.length - 3} weitere`}
                  </Typography>
                )}
                {set.items.tags && set.items.tags.length > 0 && (
                  <Typography variant="caption" color="textSecondary">
                    <strong>Tags ({set.items.tags.length}):</strong> {set.items.tags.join(', ')}
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 1 }} />

              <Typography variant="caption" color="textSecondary">
                Erstellt von: {set.createdBy}
              </Typography>
            </Paper>
          ))}

          {/* Show message if no sets match the filter */}
          {filteredSets.length === 0 && (
            <Alert severity="info">
              Keine Sets in dieser Kategorie vorhanden.
            </Alert>
          )}
        </Box>

        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>Hinweis:</strong> In der vollständigen Version können Sie hier eigene Sets erstellen, bearbeiten und löschen.
          </Typography>
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Schließen
        </Button>
        <Button variant="contained" startIcon={<AddIcon />} disabled>
          Neues Set erstellen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SetManagementDialog;
